// "Show unlinked" — last-mile clean-up check for a drawing's graph. Finds what
// still isn't wired into it (rejected items are ignored throughout):
//
//   text label     — names/describes nothing: no outgoing names / describes
//                    (or legacy labels) edge. Text on a P&ID is the name or
//                    description of something; drawing-level text (title block,
//                    notes) has no home yet and is flagged too until it does.
//   pipe junction  — needs at least one process line in AND one out; a junction
//                    with only an in (or only an out) is a stranded pipe end.
//                    Drawings end in page connectors, never junctions.
//   other symbol   — no process connection (flows to / controls) at either end.
//   connection     — missing its source or target (an orphan).

// Annotation links (text → thing), as opposed to process lines (piping, signals).
export const ANNOTATION_RELATIONSHIPS = new Set(['names', 'describes', 'labels']);

const idOf = (v) => (v && typeof v === 'object' ? (v._id != null ? String(v._id) : null) : v != null ? String(v) : null);

export const UNLINKED_REASONS = {
  text: 'Text that names or describes nothing',
  junction: 'Junction needs a pipe in and a pipe out',
  symbol: 'No pipe or control connection',
  // Drawn with the Element tool (a Generic Node by default) around text, then
  // linked as names/describes: it's text, so it should be typed as one.
  textTypedAsSymbol: "Names or describes something but isn't typed as Text Label",
  connection: 'Connection is missing an end',
  annotation: "Doesn't describe the pipe it annotates",
};

// Things that annotate rather than connect — they describe what they sit on
// (a pipe gradient describes its pipe), linked by 'describes' like text.
export const DESCRIBING_SYMBOL_TYPES = new Set(['pipe_gradient']);
export const DESCRIBING_TYPES = new Set(['text_label', ...DESCRIBING_SYMBOL_TYPES]);

// Returns { elements: Map(id → reason), connections: Map(id → reason), ordered: [{ type, item, reason }] }
// with `ordered` in the same elements-then-connections order the side pane lists them.
// Elements that are never expected to be wired into the graph: drawing notes
// comment on the plant as a whole, not on what they happen to sit next to.
export const FREE_FLOATING_TYPES = new Set(['drawing_note']);

export function findUnlinked(elements = [], connections = []) {
  const live = connections.filter((c) => !c.metadata?.rejected);
  const into = new Map();
  const outOf = new Map();
  const annotates = new Set();
  const bump = (m, k) => k && m.set(k, (m.get(k) || 0) + 1);

  for (const c of live) {
    const source = idOf(c.source);
    const target = idOf(c.target);
    if (ANNOTATION_RELATIONSHIPS.has(c.relationshipType)) {
      if (source && target) annotates.add(source);
    } else if (source && target && c.targetType !== 'Connection') {
      bump(outOf, source);
      bump(into, target);
    }
  }

  const unlinkedElements = new Map();
  const unlinkedConnections = new Map();
  const ordered = [];

  for (const el of elements) {
    if (el.metadata?.rejected || FREE_FLOATING_TYPES.has(el.elementType)) continue;
    const id = String(el._id);
    let reason = null;
    if (el.elementType === 'text_label') {
      if (!annotates.has(id)) reason = UNLINKED_REASONS.text;
    } else if (DESCRIBING_SYMBOL_TYPES.has(el.elementType)) {
      if (!annotates.has(id)) reason = UNLINKED_REASONS.annotation;
    } else if (el.elementType === 'pipe_junction') {
      if (!into.get(id) || !outOf.get(id)) reason = UNLINKED_REASONS.junction;
    } else if (!into.get(id) && !outOf.get(id)) {
      reason = annotates.has(id) ? UNLINKED_REASONS.textTypedAsSymbol : UNLINKED_REASONS.symbol;
    }
    if (reason) {
      unlinkedElements.set(id, reason);
      ordered.push({ type: 'element', item: el, reason });
    }
  }

  for (const c of live) {
    if (!idOf(c.source) || !idOf(c.target) || c.metadata?.is_orphan) {
      unlinkedConnections.set(String(c._id), UNLINKED_REASONS.connection);
      ordered.push({ type: 'connection', item: c, reason: UNLINKED_REASONS.connection });
    }
  }

  return { elements: unlinkedElements, connections: unlinkedConnections, ordered };
}
